import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  Paper,
} from '@mui/material';
import {useFormik} from 'formik';
import {
  forwardRef,
  MutableRefObject,
  PropsWithChildren,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {
  UserGroup,
  UserGroupListQuery,
  UserGroupListResponse,
} from '../../interfaces/UserGroup';
import {Role} from '../../utils/acl';
import AccessControl from '../common/AccessControl';
import DataGrid, {DataGridColumn, DataGridRef} from '../common/DataGrid';
import {UserGroupItemDeleteButton} from './UserGroupItemDeleteButton';
import UserGroupItemUpsertButton from './UserGroupItemUpsertButton';

interface Props {}

export interface UserGroupListRef {
  fetch?: Function;
  dataGridRef: MutableRefObject<DataGridRef | null>;
}

export const UserGroupList = forwardRef<
  UserGroupListRef,
  PropsWithChildren<Props>
>((_props, ref) => {
  /*******/
  /* ref */
  /*******/
  useImperativeHandle(ref, () => ({
    fetch: () => fetchData(formik.values),
    dataGridRef,
  }));

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] = useState<UserGroupListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const fetchData = async (params: UserGroupListQuery) => {
    setFetchedInProgress(true);
    try {
      const resp = await API.get<UserGroupListResponse>(
        `${apiBaseUrl}/user-group`,
        {params}
      );
      setFetchedData(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_ug010004');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  /*************/
  /* data grid */
  /*************/
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];

  const columns: DataGridColumn<UserGroup>[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 60,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'name',
      width: 180,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'priority',
      width: 80,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'description',
      width: 240,
      resizeable: true,
      sortable: false,
    },
    {
      field: 'status',
      width: 80,
      sortable: true,
      renderCell: ({row}) => {
        return row.status === 'active' ? (
          <ToggleOnIcon color="success" />
        ) : (
          <ToggleOffIcon color="error" />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      sxHeader: {textAlign: 'right'},
      sxCell: {textAlign: 'right'},
      renderCell: ({row}) => {
        return (
          <Box display="flex" gap={1} justifyContent="end">
            <AccessControl
              roles={[Role.superUser, Role.strataManager]}
              permissions={['patch::/user-group/:id(\\d+)']}
            >
              <UserGroupItemUpsertButton
                pk={row.id}
                item={row}
                prefetch
                component={IconButton}
                componentProps={{color: 'primary', size: 'small'}}
                onSubmitted={() => fetchData(formik.values)}
              >
                <EditIcon />
              </UserGroupItemUpsertButton>
            </AccessControl>

            <AccessControl
              roles={[Role.superUser, Role.strataManager]}
              permissions={['delete::/user-group/:id(\\d+)']}
            >
              <UserGroupItemDeleteButton
                item={row}
                component={IconButton}
                componentProps={{color: 'error', size: 'small'}}
                onDeleted={() => fetchData(formik.values)}
              >
                <DeleteOutlineOutlinedIcon />
              </UserGroupItemDeleteButton>
            </AccessControl>
          </Box>
        );
      },
    },
  ];

  const formik = useFormik<UserGroupListQuery>({
    initialValues: {
      page: 0,
      limit: 25,
      order: 'id',
      dir: 'DESC',
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    fetchData(formik.values);
  }, [formik.values]);

  return (
    <Paper
      sx={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ))}

      <DataGrid
        ref={dataGridRef}
        rows={rows}
        columns={columns}
        loading={fetchedInProgress}
        pagination
        paginationMode="server"
        size="small"
        sortBy={{
          field: formik.values.order,
          dir: formik.values.dir === 'DESC' ? 'desc' : 'asc',
        }}
        sortingMode="server"
        page={formik.values.page}
        pageSize={formik.values.limit}
        rowCount={fetchedData?.count}
        onPageChange={(v) => formik.setFieldValue('page', v)}
        onPageSizeChange={(v) => {
          formik.setFieldValue('limit', v);
          formik.setFieldValue('page', 0);
        }}
        onSort={(v) => {
          if (v) {
            formik.setFieldValue('order', v.field);
            formik.setFieldValue('dir', v.dir.toUpperCase());
          }
        }}
      />
    </Paper>
  );
});
